import { useEffect, useState } from "react";
import { getNicheMeta } from "../components/niche";

const SEQUENCE = [3, 18, 41, 63, 79, 90, 94, 97, 99, 100];
const TICK_MS = 420;
const HOLD_MS = 2800;

/**
 * Mobile-only demo: first-100 window filling to lock.
 * Pass a live/fallback drop card shape ({ avatar, name, title, niche, max }).
 */
export default function HeroCounterLock({ drop }) {
  const [index, setIndex] = useState(0);
  const [locked, setLocked] = useState(false);
  const [active, setActive] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mobileMq = window.matchMedia("(max-width: 999px)");
    const motionMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      setActive(mobileMq.matches);
      setReduceMotion(motionMq.matches);
    };
    apply();
    mobileMq.addEventListener?.("change", apply);
    motionMq.addEventListener?.("change", apply);
    return () => {
      mobileMq.removeEventListener?.("change", apply);
      motionMq.removeEventListener?.("change", apply);
    };
  }, []);

  useEffect(() => {
    if (!active || !drop) return undefined;

    if (reduceMotion) {
      setIndex(SEQUENCE.length - 1);
      setLocked(true);
      return undefined;
    }

    let cancelled = false;
    let timer;

    const run = () => {
      setLocked(false);
      setIndex(0);
      let i = 0;

      const tick = () => {
        if (cancelled) return;
        i += 1;
        if (i >= SEQUENCE.length) {
          setIndex(SEQUENCE.length - 1);
          setLocked(true);
          timer = window.setTimeout(() => {
            if (!cancelled) run();
          }, HOLD_MS);
          return;
        }
        setIndex(i);
        if (SEQUENCE[i] >= 100) {
          setLocked(true);
          timer = window.setTimeout(() => {
            if (!cancelled) run();
          }, HOLD_MS);
          return;
        }
        timer = window.setTimeout(tick, TICK_MS);
      };

      timer = window.setTimeout(tick, TICK_MS);
    };

    run();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [active, reduceMotion, drop]);

  if (!drop) return null;

  const views = SEQUENCE[index] ?? 100;
  const max = drop.max || 100;
  const pct = Math.min(100, Math.round((views / max) * 100));
  const nicheMeta = getNicheMeta(drop.niche);

  return (
    <div className={`lp-counter-card${locked ? " is-locked" : ""}`} aria-hidden="true">
      <div className="lp-counter-card-inner">
        <div className="lp-counter-head">
          <img src={drop.avatar} alt="" className="lp-counter-avatar lp-photo" />
          <div className="lp-counter-meta">
            <strong>{drop.name}</strong>
            <span>{drop.title}</span>
          </div>
          <span
            className="lp-counter-niche"
            style={{
              color: `var(--niche-${nicheMeta.key}-fg)`,
              background: `var(--niche-${nicheMeta.key}-bg)`,
            }}
          >
            {nicheMeta.browseLabel || nicheMeta.label}
          </span>
        </div>

        <div className="lp-counter-stage">
          <div className={`lp-counter-readout${locked && !reduceMotion ? " is-blurred" : ""}`}>
            <span className="lp-counter-num">{views}</span>
            <span className="lp-counter-den">/{max}</span>
          </div>
          {locked && (
            <div className="lp-counter-badge">
              <span className="lp-counter-badge-lock" aria-hidden="true" />
              Window closed
            </div>
          )}
        </div>

        <div className="lp-counter-bar">
          <div className="lp-counter-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="lp-counter-foot">
          {locked ? "100/100 · Closed" : `${max - views} spots left`}
        </div>
      </div>
      <p className="lp-counter-caption">
        Watch a real spot fill — the first-100 window works exactly like this on every drop
      </p>
    </div>
  );
}
